<template>
  <div class="notification-form-container">
    <h1 class="page-title">Gestion des Notifications</h1>
    <!-- Calendar Card replacing the form-card -->
    <v-card class="form-card">
      <v-card-title class="d-flex align-center justify-space-between py-3 px-4">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-calendar-month</v-icon>
          <span class="card-title font-weight-bold text-h6 primary--text">Calendrier des Notifications</span>
        </div>
        <div class="d-flex align-center">
          <v-btn outlined small class="mr-2" color="grey darken-2" @click="setToday">
            Aujourd'hui
          </v-btn>
          <v-btn fab text x-small color="grey darken-2" class="mr-1" @click="prev">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn fab text x-small color="grey darken-2" class="mr-4" @click="next">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <v-select v-model="calendarType" :items="calendarTypes" dense outlined hide-details
            class="mr-4 select-calendar-type" style="max-width: 120px"></v-select>

          <v-btn color="primary" @click="openScheduleDialog(null)">
            <v-icon left>mdi-plus</v-icon>
            Programmer
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <div class="calendar-title-display text-h5 font-weight-medium text-center my-3 grey--text text--darken-2">
          {{ calendarTitle }}
          <div class="text-subtitle-2 grey--text mt-1" v-if="calendarStart && calendarEnd">
            Période : du {{ formatSimpleDate(calendarStart) }} au {{ formatSimpleDate(calendarEnd) }}
          </div>
        </div>
        <!-- Légende des couleurs -->
        <div class="calendar-legend d-flex flex-wrap justify-center mb-3" style="gap: 12px;">
          <div v-for="item in eventColorLegend" :key="item.page" class="d-flex align-center" style="gap: 6px;">
            <span :style="{ backgroundColor: item.color }"
              style="display: inline-block; width: 14px; height: 14px; border-radius: 50%;"></span>
            <span class="text-caption grey--text text--darken-2">{{ item.label }}</span>
          </div>
        </div>
        <v-sheet height="600" class="calendar-sheet">
          <v-calendar ref="calendar" v-model="calendarFocus" color="primary" :events="scheduledNotifications"
            :type="calendarType" locale="fr" :weekdays="[1, 2, 3, 4, 5, 6, 0]" event-more-text="{0} de plus"
            @click:event="showEventDetails" @click:date="onDateClick" @change="onCalendarChange"></v-calendar>
        </v-sheet>
      </v-card-text>
    </v-card>

    <!-- Dialog: Formulaire de Programmation / Envoi -->
    <v-dialog v-model="formDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="headline blue darken-2 white--text d-flex justify-space-between align-center">
          <span>{{ formDialogTitle }}</span>
          <v-btn icon dark @click="closeFormDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="form" lazy-validation>
            <!-- Success/Error Alerts inside dialog -->
            <v-alert v-if="successSendNotif" type="success" class="alert" dismissible>{{
              successSendNotif
            }}</v-alert>
            <v-alert v-if="errorSendNotif" type="error" class="alert" dismissible>{{
              errorSendNotif
            }}</v-alert>

            <!-- Form Fields -->
            <v-row>
              <!-- Title Field -->
              <v-col cols="12" md="6">
                <v-text-field v-model="notifBody.title" label="Titre (FR)" outlined placeholder="Titre en français"
                  :rules="rule" required class="form-field"></v-text-field>
              </v-col>
              <!-- Title Field EN -->
              <v-col cols="12" md="6">
                <v-text-field v-model="notifBody.titleEn" label="Titre (EN)" outlined placeholder="Titre en anglais"
                  :rules="rule" required class="form-field"></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <!-- Body Field -->
              <v-col cols="12" md="6">
                <v-textarea v-model="notifBody.body" label="Contenu (FR)" auto-grow outlined rows="3" :rules="rule"
                  required class="form-field"></v-textarea>
              </v-col>
              <!-- Body Field EN -->
              <v-col cols="12" md="6">
                <v-textarea v-model="notifBody.bodyEn" label="Contenu (EN)" auto-grow outlined rows="3" :rules="rule"
                  required class="form-field"></v-textarea>
              </v-col>
            </v-row>

            <v-row>
              <!-- Page Type Selector -->
              <v-col cols="12" sm="4">
                <v-select v-model="page" :items="pageTypes" item-text="name" item-value="value" label="Page" outlined
                  clearable required @change="onPageTypeChange" class="form-field"></v-select>
              </v-col>

              <!-- External Link for LIENEXTERNE -->
              <v-col cols="12" sm="8" v-if="page === 'LIENEXTERNE'">
                <v-text-field v-model="notifBody.link" label="Saisir le lien" outlined
                  class="form-field"></v-text-field>
              </v-col>

              <template v-else-if="page && !['PREMIUM', 'AUTRE'].includes(page)">
                <!-- Sélecteur de sous-catégorie si disponible -->
                <v-col cols="12" sm="4" v-if="subCategories && subCategories.length > 0">
                  <v-select v-model="selectedSubCategoryId" :items="subCategories" :item-text="subCategoryItemText"
                    item-value="id" label="Sélectionner une série" outlined clearable :loading="loadingSubCategories"
                    @change="onSubCategoryChange" class="form-field"></v-select>
                </v-col>

                <!-- Sélecteur de thème si applicable -->
                <v-col cols="12" sm="4" v-else-if="showThemeSelector">
                  <v-select v-model="selectedTheme" :items="currentThemeOptions" label="Sélectionner un thème" outlined
                    clearable @change="onThemeChange" class="form-field"></v-select>
                </v-col>

                <!-- Item Selector -->
                <v-col cols="12" :sm="(subCategories && subCategories.length > 0) || showThemeSelector ? 4 : 8">
                  <v-select v-model="selectedItem" :items="dataFromApi || []" :item-text="itemText" item-value="id"
                    label="Sélectionner un élément" outlined return-object clearable :disabled="disableItemSelector"
                    :loading="loadingActualities" no-data-text="Aucun élément trouvé" class="form-field"></v-select>
                </v-col>
              </template>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-switch v-model="isScheduled" label="Programmer la notification ?" inset color="primary"></v-switch>
              </v-col>
              <!-- Date and Time Pickers -->
              <v-col cols="12" sm="6" v-if="isScheduled">
                <v-text-field type="datetime-local" id="scheduled" outlined v-model="notifBody.scheduledDateTime"
                  label="Date & Heure de programmation" :min="minDateTime"
                  :rules="isScheduled ? scheduledDateTimeRules : []" required />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6" class="py-0">
                <v-radio-group v-model="currentVersion" label="Version ciblée">
                  <v-radio label="Version actuelle" value="1"></v-radio>
                  <v-radio label="Ancienne version" value="0"></v-radio>
                  <v-radio label="Tous" value="-1"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" sm="6" class="py-0">
                <v-radio-group v-model="premium" label="Type d'utilisateurs">
                  <v-radio label="Premium" value="1"></v-radio>
                  <v-radio label="Non premium" value="0"></v-radio>
                  <v-radio label="Tous" value="-1"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pb-4 px-6">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeFormDialog">
            Annuler
          </v-btn>
          <v-btn color="primary" @click="validate" :disabled="loadingSendNotification || !isFormValid">
            {{ loadingSendNotification ? "En cours..." : (isScheduled ? "Programmer" : "Envoyer") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Détails de la Notification Planifiée -->
    <v-dialog v-model="detailsDialog" max-width="600px">
      <v-card v-if="selectedEvent">
        <v-card-title class="headline blue darken-3 white--text d-flex justify-space-between align-center">
          <span>Détails de la Notification Planifiée</span>
          <v-btn icon dark @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row class="mb-2">
            <v-col cols="12">
              <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Titre (FR) :</div>
              <div class="text-body-1">{{ selectedEvent.title }}</div>
            </v-col>
          </v-row>
          <v-row class="mb-2">
            <v-col cols="12">
              <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Message (FR) :</div>
              <div class="text-body-1">{{ selectedEvent.body }}</div>
            </v-col>
          </v-row>
          <v-row class="mb-2">
            <v-col cols="12">
              <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Titre (EN) :</div>
              <div class="text-body-1">{{ selectedEvent.titleEn || '-' }}</div>
            </v-col>
          </v-row>
          <v-row class="mb-2">
            <v-col cols="12">
              <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Message (EN) :</div>
              <div class="text-body-1">{{ selectedEvent.bodyEn || '-' }}</div>
            </v-col>
          </v-row>
          <v-row class="mb-2">
            <v-col cols="6">
              <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Date de programmation :</div>
              <div class="text-body-1">{{ formatDateTime(selectedEvent.scheduledDateTime) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Page cible :</div>
              <v-chip color="primary" outlined small>{{ selectedEvent }}</v-chip>
            </v-col>
          </v-row>
          <v-row class="mb-2" v-if="selectedEvent.link">
            <v-col cols="12">
              <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Lien externe :</div>
              <div class="text-body-1 text-truncate">
                <a :href="selectedEvent.link" target="_blank">{{ selectedEvent.link }}</a>
              </div>
            </v-col>
          </v-row>
          <v-row class="mb-2">
            <v-col cols="6">
              <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Premium :</div>
              <div class="text-body-1">{{ getPremiumLabel(selectedEvent.premium) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Version :</div>
              <div class="text-body-1">{{ getVersionLabel(selectedEvent.version) }}</div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pb-4 px-6">
          <v-btn color="error" outlined @click="cancelScheduledNotification(selectedEvent.id)" :loading="loadingCancel"
            v-if="selectedEvent && new Date(selectedEvent.scheduledDateTime) > new Date()">
            Annuler l'envoi
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="detailsDialog = false">
            Fermer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card class="mt-6">
      <v-card-title>Historique des notifications envoyées</v-card-title>
      <v-data-table :headers="historyHeaders" :items="historyList" :items-per-page="historyPagination.itemsPerPage"
        :page.sync="historyPagination.page" :server-items-length="historyPagination.totalItems"
        :loading="loadingHistory" loading-text="Chargement de l'historique..." class="elevation-1" show-expand
        single-expand @update:page="loadNotificationHistory">
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="expanded-row-details">
            <v-container fluid>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="detail-item">
                    <span class="detail-label">Titre (EN) :</span>
                    <span class="detail-value">{{ item.titleEn || "-" }}</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="detail-item">
                    <span class="detail-label">Message (EN) :</span>
                    <span class="detail-value">{{
                      item.messageEn || "-"
                    }}</span>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </td>
        </template>
        <template v-slot:[`item.numberTracking`]="{ item }">
          <span>{{ item.numberTracking }}</span>
        </template>
        <template v-slot:[`item.statut`]="{ item }">
          <span>{{ item.statut }}</span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import constants from "@/utils/constants";
import {
  getAllduaa,
  getAllRecitateur,
  getAllTheme,
  getActualityByCategory,
  FetchActualityByCategoryAndSubCategory,
  FetchAllSubCategoriesByCategoryId,
} from "@/views/qorani/services/services";
import axios from "axios";

export default {
  data() {
    return {
      // Calendar State
      formDialog: false,
      detailsDialog: false,
      selectedEvent: null,
      calendarType: "month",
      calendarTypes: [
        { text: "Mois", value: "month" },
        { text: "Semaine", value: "week" },
        { text: "Jour", value: "day" },
      ],
      calendarFocus: new Date().toISOString().slice(0, 10),
      calendarTitle: "",
      calendarStart: "",
      calendarEnd: "",
      scheduledNotifications: [],

      lastTitleNotificationCompleted: "",
      lastTitleNotificationSent: "",
      premium: 0,
      page: null,
      currentVersion: 0,
      dateMenu: false,
      timeMenu: false,
      isScheduled: false,
      pageTypes: [],
      subCategories: [],
      selectedSubCategoryId: null,
      loadingSubCategories: false,
      loadingActualities: false,
      themeOptions: {
        VJHJSV: ["VERSET_DU_JOUR", "HADITH_DU_JOUR", "LE_SAVIEZ_VOUS"],
        PHPT: ["PERSONNAGE", "PATRIMOINE"],
      },
      notifBody: {
        title: "",
        body: "",
        titleEn: "",
        bodyEn: "",
        link: null,
        identifier: null,
        scheduledDateTime: null,
      },
      loadingSendNotification: false,
      rule: [(v) => !!v || "Ce champ est requis"],
      errorSendNotif: "",
      successSendNotif: "",
      itemText: "title",
      selectedTheme: null,
      selectedItem: null,
      dataFromApi: null,

      historyList: [],
      loadingHistory: false,
      historyHeaders: [
        { text: "ID", value: "id" },
        { text: "Titre", value: "title" },
        { text: "Message", value: "message" },
        { text: "Nb. de clics", value: "numberTracking" },
        { text: "Statut", value: "statut" },
        { text: "", value: "data-table-expand" },
      ],
      historyPagination: {
        page: 1,
        itemsPerPage: 10,
        totalItems: 0,
      },
      loadingCancel: false,
    };
  },
  computed: {
    formDialogTitle() {
      return this.isScheduled ? "Programmer une Notification" : "Envoyer une Notification Immédiate";
    },
    showThemeSelector() {
      return ["VJHJSV", "PHPT"].includes(this.page) && (!this.subCategories || this.subCategories.length === 0);
    },
    currentThemeOptions() {
      if (this.page === "VJHJSV") return this.themeOptions.VJHJSV || [];
      if (this.page === "PHPT") return this.themeOptions.PHPT || [];
      return [];
    },
    disableItemSelector() {
      if (["AUTRE", "LIENEXTERNE", "PREMIUM"].includes(this.page)) {
        return true;
      }
      if (this.subCategories && this.subCategories.length > 0) {
        return !this.selectedSubCategoryId || this.loadingActualities;
      }
      if (this.showThemeSelector) {
        return !this.selectedTheme || this.loadingActualities;
      }
      return this.loadingActualities;
    },
    isFormValid() {
      const hasBasicFields =
        this.notifBody.title &&
        this.notifBody.body &&
        this.notifBody.titleEn &&
        this.notifBody.bodyEn;

      if (!hasBasicFields || !this.page) return false;

      const hasValidSchedule =
        !this.isScheduled ||
        (this.isScheduled && this.notifBody.scheduledDateTime && new Date(this.notifBody.scheduledDateTime) > new Date());

      if (!hasValidSchedule) return false;

      switch (this.page) {
        case "LIENEXTERNE":
          return !!this.notifBody.link;
        case "PREMIUM":
        case "AUTRE":
          return true;
        case "RECITATEUR":
          return !!(this.selectedItem && (this.selectedItem.recitateurId || this.selectedItem.id));
        case "THEME":
        case "DUAA":
          return !!(this.selectedItem && this.selectedItem.id);
        default:
          if (this.subCategories && this.subCategories.length > 0 && !this.selectedSubCategoryId) {
            return false;
          }
          if (this.showThemeSelector && !this.selectedTheme) {
            return false;
          }
          return !!(this.selectedItem && this.selectedItem.id);
      }
    },
    minDateTime() {
      const now = new Date();
      const offset = now.getTimezoneOffset();
      const localNow = new Date(now.getTime() - offset * 60 * 1000);
      return localNow.toISOString().slice(0, 16);
    },
    scheduledDateTimeRules() {
      return [
        (v) => !!v || "Ce champ est requis",
        (v) => {
          //console.log(v);
          if (!v) return true;
          return new Date(v) > new Date() || "La date et l'heure doivent être dans le futur";
        },
      ];
    },
    eventColorLegend() {
      return this.pageTypes.map((pt) => ({
        page: pt.value,
        label: pt.name,
        color: this.getEventColorForPage(pt.value),
      }));
    },
  },
  methods: {
    async loadCategories() {
      try {
        let res = await axios.get(`${constants.QORANI}actualityCategory/getall`);
        this.pageTypes = res.data.map(category => ({
          id: category.id,
          name: category.label,
          value: category.category || category.label,
          subcategories: category.subCategoryList || []
        }));
        this.pageTypes.unshift({ name: "RECITATEUR", value: "RECITATEUR", subcategories: [] });
        this.pageTypes.push(
          { name: "THEME", value: "THEME", subcategories: [] },
          { name: "DUAA", value: "DUAA", subcategories: [] },
          { name: "PREMIUM", value: "PREMIUM", subcategories: [] },
          { name: "LIENEXTERNE", value: "LIENEXTERNE", subcategories: [] },
          { name: "AUTRE", value: "AUTRE", subcategories: [] }
        );
      } catch (error) {
        console.error("Erreur lors du chargement des catégories:", error);
      }
    },
    getEventColorForPage(page) {
      const colors = {
        RECITATEUR: "#4CAF50",
        DOSSIER: "#2196F3",
        ARTICLE: "#2196F3",
        VJHJSV: "#FF9800",
        PHPT: "#9C27B0",
        THEME: "#E91E63",
        DUAA: "#00BCD4",
        PREMIUM: "#FFB300",
        LIENEXTERNE: "#795548",
        AUTRE: "#607D8B",
      };
      if (colors[page]) return colors[page];
      let hash = 0;
      for (let i = 0; i < (page || "").length; i++) {
        hash = page.charCodeAt(i) + ((hash << 5) - hash);
      }
      const c = (hash & 0x00ffffff).toString(16).toUpperCase();
      return "#" + "00000".substring(0, 6 - c.length) + c;
    },
    // Calendar Navigation and Events
    setToday() {
      this.calendarFocus = new Date().toISOString().slice(0, 10);
    },
    prev() {
      this.$refs.calendar.prev();
    },
    next() {
      this.$refs.calendar.next();
    },
    onCalendarChange({ start, end }) {
      const grid = this.getGridDates(start, end);
      this.calendarStart = grid.start;
      this.calendarEnd = grid.end;
      this.$nextTick(() => {
        if (this.$refs.calendar) {
          this.calendarTitle = this.$refs.calendar.title;
        }
        this.loadScheduledNotifications();
      });
    },
    getGridDates(start, end) {
      if (this.calendarType !== "month" || !start || !end) {
        return {
          start: start ? start.date : "",
          end: end ? end.date : "",
        };
      }
      try {
        const startDate = new Date(start.date);
        // Map JS Sunday=0, Monday=1 to Grid Monday=0, Sunday=6
        const startDayOfWeek = (startDate.getDay() + 6) % 7;
        const gridStartDate = new Date(startDate);
        gridStartDate.setDate(startDate.getDate() - startDayOfWeek);

        const endDate = new Date(end.date);
        const endDayOfWeek = (endDate.getDay() + 6) % 7;
        const gridEndDate = new Date(endDate);
        gridEndDate.setDate(endDate.getDate() + (6 - endDayOfWeek));

        const toISO = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        return {
          start: toISO(gridStartDate),
          end: toISO(gridEndDate),
        };
      } catch (e) {
        console.error("Error calculating grid dates:", e);
        return {
          start: start ? start.date : "",
          end: end ? end.date : "",
        };
      }
    },
    openScheduleDialog(date) {
      this.resetForm();
      this.isScheduled = true;
      if (date) {
        const now = new Date();
        const timePart = now.toTimeString().slice(0, 5); // HH:mm
        this.notifBody.scheduledDateTime = `${date}T${timePart}`;
      } else {
        const now = new Date();
        const offset = now.getTimezoneOffset();
        const localNow = new Date(now.getTime() - offset * 60 * 1000);
        this.notifBody.scheduledDateTime = localNow.toISOString().slice(0, 16);
      }
      this.formDialog = true;
    },
    onDateClick({ date }) {
      const clickedDate = new Date(date);
      clickedDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (clickedDate < today) {
        return; // Ne pas ouvrir si la date est dans le passé
      }
      this.openScheduleDialog(date);
    },
    closeFormDialog() {
      this.resetForm();
      this.formDialog = false;
      this.successSendNotif = "";
      this.errorSendNotif = "";
    },
    showEventDetails({ event }) {
      this.selectedEvent = event;
      this.detailsDialog = true;
    },
    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return "-";
      try {
        const date = new Date(dateTimeStr);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${day}/${month}/${year} à ${hours}:${minutes}`;
      } catch (e) {
        return dateTimeStr;
      }
    },
    formatSimpleDate(dateStr) {
      if (!dateStr) return "";
      try {
        const [year, month, day] = dateStr.split("-");
        return `${day}/${month}/${year}`;
      } catch (e) {
        return dateStr;
      }
    },
    getPremiumLabel(val) {
      switch (String(val)) {
        case "1":
          return "Premium";
        case "0":
          return "Non premium";
        default:
          return "Tous";
      }
    },
    getVersionLabel(val) {
      switch (String(val)) {
        case "1":
          return "Version actuelle";
        case "0":
          return "Ancienne version";
        default:
          return "Tous";
      }
    },
    
    async loadScheduledNotifications() {
      let backendScheduled = [];
      if (this.calendarStart && this.calendarEnd) {
        try {
          const payload = {
            startDate: `${this.calendarStart}T00:00:00Z`,
            endDate: `${this.calendarEnd}T23:59:59Z`,
          };
          const response = await axios.post(
            `${constants.QORANI}notification/getAllPerMonth`,
            payload,
          );

          const list = Array.isArray(response.data) ? response.data : [];

          backendScheduled = list.map(item => {
            const dateTimeStr = item.dateOfSending;
            const d = new Date(dateTimeStr);
            const localStart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

            return {
              id: String(item.id),
              name: item.title,
              start: localStart,
              title: item.title,
              body: item.message || "",
              titleEn: item.titleEn || "",
              bodyEn: item.messageEn || "",
              page: item.page || "AUTRE",
              link: item.link || null,
              premium: item.premium,
              version: item.currentVersion,
              scheduledDateTime: dateTimeStr,
              color: this.getEventColorForPage(item.page || "AUTRE"),
            };
          });
        } catch (error) {
          console.warn("Error fetching scheduled notifications from backend:", error);
        }
      }

      this.scheduledNotifications = backendScheduled;
    },

    async loadNotificationHistory() {
      try {
        this.loadingHistory = true;
        const payload = {
          page: this.historyPagination.page - 1,
          size: this.historyPagination.itemsPerPage,
          filter: 0,
        };
        const response = await axios.post(
          `${constants.QORANI}notification/getall`,
          payload,
        );
        const data = response.data;
        //console.log(data);

        this.historyList = (data.notificationTrackingResponseList || []).map(
          (item) => ({
            ...item,
            statut: item.terminated ? "Terminé" : "En cours",
          }),
        );
        this.historyPagination.totalItems = data.totalNbrElement || 0;
      } catch (e) {
        console.error("Erreur chargement historique:", e);
      } finally {
        this.loadingHistory = false;
      }
    },

    async validate() {
      this.successSendNotif = "";
      this.errorSendNotif = "";
      // Re-check scheduled date is still in the future at submission time
      if (this.isScheduled && this.notifBody.scheduledDateTime) {
        const scheduledDate = new Date(this.notifBody.scheduledDateTime);
        if (scheduledDate <= new Date()) {
          this.errorSendNotif = "Impossible de programmer une notification dans le passé. Veuillez choisir une date et heure futures.";
          // Re-validate the form to show field-level error
          this.$refs.form.validate();
          return;
        }

        if (this.selectedItem && this.selectedItem.dateOfPublish) {
          const publishDate = new Date(this.selectedItem.dateOfPublish);
          if (scheduledDate <= publishDate) {
            this.errorSendNotif = "La date de notification doit être après la date de publication de l'article.";
            this.$refs.form.validate();
            return;
          }
        }
      }
      if (this.selectedItem && this.selectedItem.published == false && !this.isScheduled) {
        this.errorSendNotif = "Veuillez publier l'article avant d'envoyer une notification.";
        this.$refs.form.validate();
        return
      }
      if (this.$refs.form.validate()) {
        this.loadingSendNotification = true;
        await this.sendNotification();
      }
    },
    async sendNotification() {
      this.errorSendNotif = "";
      try {
        var currentVersion;
        switch (this.currentVersion) {
          case "-1":
            currentVersion = null;
            break;
          case "0":
            currentVersion = false;
            break;
          default:
            currentVersion = true;
            break;
        }
        var premium;
        switch (this.premium) {
          case "-1":
            premium = null;
            break;
          case "0":
            premium = false;
            break;
          default:
            premium = true;
            break;
        }
        // Convert date and time to Unix timestamp (seconds)
        let nbrSecondLeft = null;
        if (this.isScheduled && this.notifBody.scheduledDateTime) {
          const dateTime = new Date(this.notifBody.scheduledDateTime);
          const scheduledTimestamp = Math.floor(dateTime.getTime() / 1000);
          const unixTimestamp = Math.floor(Date.now() / 1000);
          nbrSecondLeft = scheduledTimestamp - unixTimestamp;
        }
        let identifier = null;
        if (this.page === "RECITATEUR") {
          identifier = this.selectedItem?.recitateurId || this.selectedItem?.id || null;
        } else if (["AUTRE", "LIENEXTERNE", "PREMIUM"].includes(this.page)) {
          identifier = null;
        } else {
          identifier = this.selectedItem?.id || null;
        }

        if (this.page == null) {
          this.errorSendNotif = "Veuillez sélectionner une page.";
          return;
        }

        if (this.page === "autre" || this.page === "AUTRE") {
          this.selectedItem = null;
        }
        let type = null;
        if (!["RECITATEUR", "THEME", "DUAA", "PREMIUM", "LIENEXTERNE", "AUTRE"].includes(this.page)) {
          type = "DOSSIER"
        }
        const payload = {
          title: this.notifBody.title,
          body: this.notifBody.body,
          page: type ? type : this.page,
          link: this.notifBody.link,
          identifier: identifier,
          premium: premium,
          currentVersion: currentVersion,
          nbrSecondLeft: this.isScheduled ? nbrSecondLeft : 0,
          titleEn: this.notifBody.titleEn,
          bodyEn: this.notifBody.bodyEn,
        };
        let res = await axios.post(
          `${constants.QORANI}notification/add`,
          payload,
        );
        const newNotif = {
          id: res.data.id,
          title: res.data.title,
          message: res.data.message,
          numberTracking: 0,
          statut: "En cours",
        };
        payload.notificationTrackingId = res.data.id,
          //console.log(payload.notificationTrackingId);
        this.historyList.unshift(newNotif);
        let response = await axios.post(
          `${constants.QORANI}notification/send`,
          payload,
        );

        if (response.data.success) {
          this.resetForm();
          this.successSendNotif = "Notification envoyée avec succès.";
          this.historyList[0].statut = "Envoyé";
          this.loadNotificationHistory();
        } else {
          this.errorSendNotif =
            "Une erreur s'est produite lors de l'envoi de la notification.";
        }
      } catch (error) {
        console.error(error);
        this.errorSendNotif = "Erreur: " + error.message;
      } finally {
        this.loadingSendNotification = false;
        await this.loadScheduledNotifications();
      }
    },
    async cancelScheduledNotification(id) {
      if (!confirm("Voulez-vous vraiment annuler l'envoi de cette notification programmée ?")) {
        return;
      }
      this.loadingCancel = true;
      try {
        const response = await axios.get(
          `${constants.QORANI}notification/delete/${id}`
        );
        if (response.status === 200 || response.data.success || response.data) {
          this.detailsDialog = false;
          await this.loadScheduledNotifications();
        }
      } catch (error) {
        console.error("Erreur lors de l'annulation:", error);
        alert("Erreur lors de l'annulation de la notification.");
      } finally {
        this.loadingCancel = false;
      }
    },
    async onPageTypeChange(val) {
      //console.log("onPageTypeChange:", val);

      const selectedType = typeof val === "object" && val !== null
        ? val
        : this.pageTypes.find((p) => p.value === val || p.name === val);

      const typeValue = selectedType ? selectedType.value : val;

      this.dataFromApi = null;
      this.selectedItem = null;
      this.selectedTheme = null;
      this.selectedSubCategoryId = null;
      this.notifBody.link = null;
      this.notifBody.identifier = null;
      this.page = typeValue;

      if (!typeValue) {
        this.subCategories = [];
        return;
      }

      this.subCategories = (selectedType && selectedType.subcategories) ? [...selectedType.subcategories] : [];

      // Si c'est une catégorie d'actualité et la liste de sous-catégories est vide, tentative de chargement via API
      if (
        selectedType &&
        selectedType.id &&
        !["RECITATEUR", "THEME", "DUAA", "PREMIUM", "LIENEXTERNE", "AUTRE"].includes(typeValue) &&
        this.subCategories.length === 0
      ) {
        try {
          this.loadingSubCategories = true;
          const subs = await FetchAllSubCategoriesByCategoryId(selectedType.id);
          if (subs && subs.length) {
            this.subCategories = subs;
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des sous-catégories:", error);
        } finally {
          this.loadingSubCategories = false;
        }
      }
      if (!["PREMIUM", "LIENEXTERNE", "AUTRE"].includes(typeValue)) {
        if (this.subCategories.length === 0 && !this.showThemeSelector) {
          await this.fetchData(typeValue);
        }
      }
    },
    async onSubCategoryChange(subCategoryId) {
      this.selectedItem = null;
      this.dataFromApi = null;
      if (!subCategoryId) return;

      try {
        this.loadingActualities = true;
        this.itemText = "title";
        const categoryCode = this.page;
        const res = await FetchActualityByCategoryAndSubCategory(categoryCode, subCategoryId);
        this.dataFromApi = Array.isArray(res) ? res : (res?.actualityMultiTypes || res?.content || []);
      } catch (error) {
        console.error("Erreur lors de la récupération des actualités:", error);
        this.errorSendNotif = "Erreur lors de la récupération des actualités";
      } finally {
        this.loadingActualities = false;
      }
    },
    subCategoryItemText(item) {
      if (!item) return "";
      return item.label || item.labelEn || item.title || item.name || "";
    },
    async onThemeChange() {
      if (!this.selectedTheme) {
        this.dataFromApi = null;
        this.selectedItem = null;
        return;
      }
      //console.log("Selected theme:", this.selectedTheme);
      try {
        this.loadingActualities = true;
        this.dataFromApi = await getActualityByCategory(this.selectedTheme);
        this.itemText = "title";
        this.selectedItem = null;
      } catch (error) {
        console.error("Erreur lors de la récupération du thème:", error);
      } finally {
        this.loadingActualities = false;
      }
    },
    async fetchData(pageType) {
      this.dataFromApi = null;
      this.selectedItem = null;

      try {
        this.loadingActualities = true;
        switch (pageType) {
          case "THEME":
            this.dataFromApi = await getAllTheme();
            this.itemText = "title";
            break;
          case "DUAA":
            this.dataFromApi = await getAllduaa();
            this.itemText = "title";
            break;
          case "RECITATEUR":
            this.dataFromApi = await getAllRecitateur();
            this.itemText = "name";
            break;
          default:
            this.itemText = "title";
            if (this.showThemeSelector && this.selectedTheme) {
              this.dataFromApi = await getActualityByCategory(this.selectedTheme);
            } else {
              this.dataFromApi = await getActualityByCategory(pageType);
            }
            break;
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      } finally {
        this.loadingActualities = false;
      }
    },
    resetForm() {
      this.notifBody = {
        title: "",
        body: "",
        titleEn: "",
        bodyEn: "",
        link: null,
        identifier: null,
        scheduledDateTime: null,
      };
      this.page = null;
      this.selectedTheme = null;
      this.subCategories = [];
      this.selectedSubCategoryId = null;
      this.selectedItem = null;
      this.dataFromApi = null;
      this.isScheduled = false;
      this.dateMenu = false;
      this.timeMenu = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    async getLastNotificationCompleted() {
      this.loading = true;
      try {
        const response = await axios.post(
          constants.QORANI + "notificationState/getallPaginate",
          {
            pageNumber: 0,
            pageSize: 1,
          },
        );
        this.lastTitleNotificationCompleted =
          response.data.notificationStateResponseList[0]?.actualityTitle ||
          null;
      } catch (error) {
        console.error("Error fetching notification:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async checkSendNotification() {
      this.lastTitleNotificationSent = this.historyList[0]?.title || null;
      if (
        this.lastTitleNotificationSent != this.lastTitleNotificationCompleted
      ) {
        this.historyList[0].statut = "En cours";
      }
    },
  },
  async mounted() {
  try {
    await Promise.all([
      this.loadCategories(),
      this.loadScheduledNotifications(),
      this.getLastNotificationCompleted(),
      this.loadNotificationHistory(),
    ]);

    this.checkSendNotification();
  } catch (error) {
    console.error("Erreur lors de l'initialisation :", error);
  }
}
};
</script>

<style scoped>
.notification-form-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.calendar-sheet {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.page-title {
  font-size: 2rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.form-card {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 16px;
}

.card-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1976d2;
}

.card-text {
  padding: 16px;
}

.alert {
  margin-bottom: 16px;
}

.form-field {
  margin-bottom: 8px;
}

.button-container {
  display: flex;
  justify-content: flex-end;
}

.submit-button {
  text-transform: uppercase;
  font-weight: 500;
}

.expanded-row-details {
  background-color: #f9f9f9;
}

.detail-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}
</style>
